import { Col, Flex, Progress, Row, Spin } from 'antd';

import { Link } from '@suntex.core/nav';
import {
  Database_Procs_PlayerVIF_Item,
  Database_Procs_VIFAggregatedByLevel_Item,
} from '@suntex.fim/api-client';
import { $ArithmeticOperation, ArithmeticOperation } from '@suntex.fim/enums';
import { ProgressBar } from '@suntex.fim/ui';

import { vifHighlight } from '../const';
import { getVifGamePath, getVifGames } from '../utils';

import { SummaryProgressProps } from './types';

const FACT_HEIGHT = 24;

/** Configuration for each operation column */
interface OperationConfig {
  label: string;
  operation: ArithmeticOperation;
  teamKeys: {
    first: keyof Database_Procs_VIFAggregatedByLevel_Item;
    second: keyof Database_Procs_VIFAggregatedByLevel_Item;
    third: keyof Database_Procs_VIFAggregatedByLevel_Item;
  };
  firstFactLabel: string;
}

const OPERATIONS: OperationConfig[] = [
  {
    label: $ArithmeticOperation.describe(ArithmeticOperation.Addition).label,
    operation: ArithmeticOperation.Addition,
    teamKeys: {
      first: 'vif_add_completed_count',
      second: 'vif_add_rules_dd_completed_count',
      third: 'vif_add_1_completed_count',
    },
    firstFactLabel: '64 Facts',
  },
  {
    label: $ArithmeticOperation.describe(ArithmeticOperation.Subtraction).label,
    operation: ArithmeticOperation.Subtraction,
    teamKeys: {
      first: 'vif_sub_completed_count',
      second: 'vif_sub_rules_dd_completed_count',
      third: 'vif_sub_2_completed_count',
    },
    firstFactLabel: '64 Facts',
  },
  {
    label: $ArithmeticOperation.describe(ArithmeticOperation.Multiplication)
      .label,
    operation: ArithmeticOperation.Multiplication,
    teamKeys: {
      first: 'vif_mul_rules_dd_2_completed_count',
      second: 'vif_mul_completed_count',
      third: 'vif_mul_1_completed_count',
    },
    firstFactLabel: '36 Facts',
  },
  {
    label: $ArithmeticOperation.describe(ArithmeticOperation.Division).label,
    operation: ArithmeticOperation.Division,
    teamKeys: {
      first: 'vif_div_completed_count',
      second: 'vif_div_rules_dd_completed_count',
      third: 'vif_div_1_completed_count',
    },
    firstFactLabel: '36 Facts',
  },
];

/** Renders a single team facts column */
function TeamFactsColumn({
  config,
  teamData,
  showData,
  isLast,
  playerHubPath,
}: {
  config: OperationConfig;
  teamData: Partial<Database_Procs_VIFAggregatedByLevel_Item>;
  showData: boolean;
  isLast: boolean;
  playerHubPath?: string;
}) {
  return (
    <Col className={`gutter-row${isLast ? '' : ' border-right'}`} span={6}>
      <Flex vertical>
        <span className="skill-set-header border-bottom">
          {playerHubPath ? (
            <Link to={getVifGamePath(config.operation)}>{config.label}</Link>
          ) : (
            config.label
          )}
        </span>
        <Flex>
          <div className="player-chart-gym skill-set-header important-fact-graph border-right">
            <span>{config.firstFactLabel}</span>
            <ProgressBar
              percent={Number(teamData[config.teamKeys.first]) || 0}
              fill="veryImportantFacts"
              showData={showData}
            />
          </div>
          <div className="player-chart-gym skill-set-header important-fact-graph border-right">
            <span>100 Facts</span>
            <ProgressBar
              percent={Number(teamData[config.teamKeys.second]) || 0}
              fill="veryImportantFacts"
              showData={showData}
            />
          </div>
          <div className="player-chart-gym skill-set-header important-fact-graph">
            <span>10k Facts</span>
            <ProgressBar
              percent={Number(teamData[config.teamKeys.third]) || 0}
              fill="veryImportantFacts"
              showData={showData}
            />
          </div>
        </Flex>
      </Flex>
    </Col>
  );
}

/** Renders a single player facts column */
function PlayerFactsColumn({
  config,
  playerData,
  playerHubPath,
}: {
  config: OperationConfig;
  playerData: Partial<Database_Procs_PlayerVIF_Item>;
  playerHubPath?: string;
}) {
  return (
    <Col className="gutter-row border-right" span={6}>
      <Flex vertical>
        <span className="skill-set-header border-bottom">
          {playerHubPath ? (
            <Link to={getVifGamePath(config.operation)}>{config.label}</Link>
          ) : (
            config.label
          )}
        </span>
        <Flex>
          <Flex className="player-chart-gym skill-set-header important-fact-graph">
            {Object.entries(getVifGames(config.operation)).map(([newKey]) => {
              const isHighlightGames = vifHighlight.includes(newKey);
              const value = Number(
                playerData[newKey as keyof Database_Procs_PlayerVIF_Item] || 0,
              );
              return (
                <Progress
                  key={newKey}
                  trailColor={isHighlightGames ? '#333333b3' : '#1d1d1d4d'}
                  strokeColor={isHighlightGames ? '#83E2B8' : '#3FB2FF'}
                  percent={value}
                  showInfo={false}
                  size={['100%', FACT_HEIGHT]}
                  className="custom-step-progress progress-bar-color"
                />
              );
            })}
          </Flex>
        </Flex>
      </Flex>
    </Col>
  );
}

export function VifSummaryProgress({
  teamData,
  playerData,
  showData = true,
  showTeamRow = true,
  playerHubPath,
}: SummaryProgressProps<
  Database_Procs_VIFAggregatedByLevel_Item,
  Database_Procs_PlayerVIF_Item
>) {
  if (teamData === null || playerData === null) return <Spin />;

  return (
    <div className="graph-container">
      {showTeamRow && (
        <>
          {playerData && <div className="team-label">Team</div>}
          <Row className="very-important-facts team-facts">
            {OPERATIONS.map((config, index) => (
              <TeamFactsColumn
                key={config.label}
                config={config}
                teamData={teamData}
                showData={showData}
                isLast={index === OPERATIONS.length - 1}
                playerHubPath={playerHubPath}
              />
            ))}
          </Row>
        </>
      )}
      {playerData && (
        <>
          <div className="team-label">Player</div>
          <Row className="very-important-facts">
            {OPERATIONS.map((config) => (
              <PlayerFactsColumn
                key={config.label}
                config={config}
                playerData={playerData}
                playerHubPath={playerHubPath}
              />
            ))}
          </Row>
        </>
      )}
    </div>
  );
}
